type FN = () => any;
export type UseAsyncFetchOnCancel = (fn: FN) => void;
export type UseAsyncFetchFun<T = any> = (
  cancelCallback: UseAsyncFetchOnCancel,
) => Promise<T>;


export type UseAsyncFetch<T = any> = {
  response: Ref<T>;
  loading: Ref<boolean>;
  error: Ref<unknown | null>;
  refresh: () => Promise<void>;
};


export function useAsyncFetch<T = any>(fun: UseAsyncFetchFun<T>, defaultValue: T): UseAsyncFetch<Exclude<T, undefined | null>>
export function useAsyncFetch<T = any>(fun: UseAsyncFetchFun<T>, defaultValue?: T): UseAsyncFetch<T | undefined | null>
export function useAsyncFetch<T = any>(
  fun: UseAsyncFetchFun<T>,
  defaultValue?: T 
): UseAsyncFetch<T> {
  const response = ref<T>(defaultValue as unknown as any) as Ref<T>;
  const loading = ref<boolean>(true);
  const lastError = ref<unknown | null>(null);

  const started = shallowRef(true);
  let counter: number = 0;


  const fetchData = async (onInvalidate: (fn: () => any) => any = () => {}) => {
    if (!started.value) return;

    counter++;
    const counterAtBeginning = counter;
    let hasFinished = false;

    Promise.resolve().then(() => {
      loading.value = true;
    });

    try {
      const result = await fun((cancelCallback) => {
        onInvalidate(() => {
          loading.value = false;

          if (!hasFinished) cancelCallback();
        });
      });

      if (counterAtBeginning === counter) {
        response.value = result;
      }
    } catch (e) {
      console.error(e);
      lastError.value = e;
    } finally {
      if (counterAtBeginning === counter) {
        loading.value = false;
      }

      hasFinished = true;
    }
  }

  watchEffect(async (onInvalidate) => {
    await fetchData(onInvalidate);
  });

  const refresh = async () => {
    await fetchData();
  }

  return {
    response,
    loading,
    error: lastError,
    refresh: refresh
  };
};
